import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../environments/environment';

import {
    PlanningAssignment,
    PlanningAssignmentDto,
    PlanningAssignmentSaveDto,
    ResourceCategory,
    ResourceCustomer,
    ResourceEmployee,
    ResourceEmployeeDto,
    ResourceKnowledge,
    ResourcePlant,
    ResourceWeekendDay,
    ResourceWeekendHoursDto,
    ResourceExternalEvent,
    ResourceHiddenExternalEvent,
    ResourceHideExternalEventRequest,
    ResourceMyCalendarContext,
} from '../Models/resource.models';

/**
 * Anbindung der Ressourcenplanung an das Backend (api/resourceplanning).
 *
 * Ersetzt den bisherigen Mock-Service. Der Token wird vom bestehenden
 * AuthInterceptor angehängt. Datum/Uhrzeit werden als lokale Wandzeit ohne
 * Zeitzone übertragen (yyyy-MM-ddTHH:mm:ss), genau wie in der bestehenden
 * Ressourcenplanung.
 */
@Injectable({
    providedIn: 'root'
})
export class ResourceApiService {

    private readonly apiUrl = environment.apiBaseUrl + '/resourceplanning';

    private readonly palette = [
        '#2563eb', '#7c3aed', '#0891b2', '#059669',
        '#d97706', '#dc2626', '#db2777', '#4f46e5',
        '#0d9488', '#9333ea', '#ea580c', '#1d4ed8'
    ];

    constructor(private http: HttpClient) { }

    // ---- Stammdaten ------------------------------------------------------

    getEmployees(): Observable<ResourceEmployee[]> {
        return this.http
            .get<ResourceEmployeeDto[]>(`${this.apiUrl}/employees`)
            .pipe(map(list => list.map(dto => this.toEmployee(dto))));
    }

    getKnowledge(): Observable<ResourceKnowledge[]> {
        return this.http.get<ResourceKnowledge[]>(`${this.apiUrl}/skills`);
    }

    getCategories(): Observable<ResourceCategory[]> {
        return this.http.get<ResourceCategory[]>(`${this.apiUrl}/categories`);
    }

    getCustomers(): Observable<ResourceCustomer[]> {
        return this.http.get<ResourceCustomer[]>(`${this.apiUrl}/customers`);
    }

    getPlants(customerId?: number): Observable<ResourcePlant[]> {
        let params = new HttpParams();
        if (customerId) {
            params = params.set('customerId', customerId);
        }
        return this.http.get<ResourcePlant[]>(`${this.apiUrl}/equipments`, { params });
    }

    // ---- Termine -------------------------------------------------------------

    /**
     * Termine, die den Zeitraum berühren. from/to als yyyy-MM-dd (ganze Tage).
     */
    getAssignments(fromDate: string, toDate: string): Observable<PlanningAssignment[]> {
        const params = new HttpParams()
            .set('from', `${fromDate}T00:00:00`)
            .set('to', `${toDate}T23:59:59`);

        return this.http
            .get<PlanningAssignmentDto[]>(`${this.apiUrl}/assignments`, { params })
            .pipe(map(list => list.map(dto => this.fromDto(dto))));
    }
    /** Teambezogene Termine; die Backend-API prüft die Team-Berechtigung. */
    getTeamAssignments(teamId: number, fromDate: string, toDate: string): Observable<PlanningAssignment[]> {
        const params = new HttpParams()
            .set('from', `${fromDate}T00:00:00`)
            .set('to', `${toDate}T23:59:59`);
        return this.http.get<PlanningAssignmentDto[]>(
            `${this.apiUrl}/team-planning/${teamId}/assignments`, { params }
        ).pipe(map(items => items.map(dto => this.fromDto(dto))));
    }

    saveTeamAssignment(teamId: number, assignment: PlanningAssignment): Observable<PlanningAssignment> {
        const payload = this.toSaveDto({ ...assignment, sendOutlookInvitation: false });
        const url = `${this.apiUrl}/team-planning/${teamId}/assignments`;
        const request$ = assignment.id > 0
            ? this.http.put<PlanningAssignmentDto>(`${url}/${assignment.id}`, payload)
            : this.http.post<PlanningAssignmentDto>(url, payload);
        return request$.pipe(map(dto => this.fromDto(dto)));
    }

    deleteTeamAssignment(teamId: number, id: number): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/team-planning/${teamId}/assignments/${id}`);
    }

    /** Outlook-Termine der Teammitglieder (gleiche Filter wie in der Gesamtplanung). */
    getTeamExternalEvents(teamId: number, fromDate: string, toDate: string): Observable<ResourceExternalEvent[]> {
        const params = new HttpParams()
            .set('from', `${fromDate}T00:00:00`)
            .set('to', `${toDate}T23:59:59`);
        return this.http.get<ResourceExternalEvent[]>(
            `${this.apiUrl}/team-planning/${teamId}/external-events`, { params });
    }

    getTeamDetail(teamId: number): Observable<{ id: number; members: Array<{ userId: string }> }> {
        return this.http.get<{ id: number; members: Array<{ userId: string }> }>(
            `${this.apiUrl}/teams/${teamId}`);
    }

    getExternalEvents(
        fromDate: string,
        toDate: string
    ): Observable<ResourceExternalEvent[]> {

        const params = new HttpParams()
            .set('from', `${fromDate}T00:00:00`)
            .set('to', `${toDate}T23:59:59`);

        return this.http.get<ResourceExternalEvent[]>(
            `${this.apiUrl}/external-events`,
            { params }
        );
    }

    // ---- Ausgeblendete Outlook-Termine (für alle Planer gültig) ----

    getHiddenExternalEvents(): Observable<ResourceHiddenExternalEvent[]> {
        return this.http.get<ResourceHiddenExternalEvent[]>(
            `${this.apiUrl}/external-events/hidden`
        );
    }

    /** Blendet ein Vorkommen aus; der Termin bleibt in Outlook und wird nicht übernommen. */
    hideExternalEvent(
        request: ResourceHideExternalEventRequest
    ): Observable<ResourceHiddenExternalEvent> {
        return this.http.post<ResourceHiddenExternalEvent>(
            `${this.apiUrl}/external-events/hidden`,
            request
        );
    }

    unhideExternalEvent(id: number): Observable<void> {
        return this.http.delete<void>(
            `${this.apiUrl}/external-events/hidden/${id}`
        );
    }

    /** Blendet alle ausgeblendeten Termine wieder ein; liefert die Anzahl. */
    unhideAllExternalEvents(): Observable<number> {
        return this.http.delete<number>(
            `${this.apiUrl}/external-events/hidden`
        );
    }

    // ---- Mein Kalender (nur eigene Daten; die Benutzer-ID ermittelt das Backend) ----
    // Aus „Mein Kalender“ wird Outlook nie verändert: keine Einladungen, Updates oder Absagen.

    getMyCalendarContext(): Observable<ResourceMyCalendarContext> {
        return this.http.get<ResourceMyCalendarContext>(`${this.apiUrl}/my-calendar/context`);
    }

    /** from/to als yyyy-MM-dd; to ist exklusiv (Tagesbeginn). */
    getMyAssignments(fromDate: string, toDate: string): Observable<PlanningAssignment[]> {
        const params = new HttpParams().set('from', `${fromDate}T00:00:00`).set('to', `${toDate}T00:00:00`);
        return this.http.get<PlanningAssignmentDto[]>(`${this.apiUrl}/my-calendar/assignments`, { params })
            .pipe(map(list => list.map(dto => this.fromDto(dto))));
    }

    /** Eigenen Termin anlegen/ändern bzw. eigenen Outlook-Vorschlag übernehmen (ohne Outlook-Versand). */
    saveMyAssignment(assignment: PlanningAssignment): Observable<PlanningAssignment> {
        const payload = this.toSaveDto({ ...assignment, sendOutlookInvitation: false });
        const url = `${this.apiUrl}/my-calendar/assignments`;
        const request$ = assignment.id > 0
            ? this.http.put<PlanningAssignmentDto>(`${url}/${assignment.id}`, payload)
            : this.http.post<PlanningAssignmentDto>(url, payload);
        return request$.pipe(map(dto => this.fromDto(dto)));
    }

    /** Löscht nur den internen Termin – nie einen Outlook-Termin. */
    deleteMyAssignment(id: number): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/my-calendar/assignments/${id}`);
    }

    /** from/to als yyyy-MM-dd; to ist exklusiv (Tagesbeginn). */
    getMyExternalEvents(fromDate: string, toDate: string): Observable<ResourceExternalEvent[]> {
        const params = new HttpParams().set('from', `${fromDate}T00:00:00`).set('to', `${toDate}T00:00:00`);
        return this.http.get<ResourceExternalEvent[]>(`${this.apiUrl}/my-calendar/external-events`, { params });
    }

    getMyHiddenExternalEvents(): Observable<ResourceHiddenExternalEvent[]> {
        return this.http.get<ResourceHiddenExternalEvent[]>(`${this.apiUrl}/my-calendar/external-events/hidden`);
    }

    hideMyExternalEvent(request: ResourceHideExternalEventRequest): Observable<ResourceHiddenExternalEvent> {
        return this.http.post<ResourceHiddenExternalEvent>(`${this.apiUrl}/my-calendar/external-events/hidden`, request);
    }

    unhideMyExternalEvent(id: number): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/my-calendar/external-events/hidden/${id}`);
    }

    unhideAllMyExternalEvents(): Observable<number> {
        return this.http.delete<number>(`${this.apiUrl}/my-calendar/external-events/hidden`);
    }

    /** Mitarbeiter-Datensatz (Zeitleiste/Editor) aus dem Backend-DTO. */
    toEmployeeFromDto(dto: ResourceEmployeeDto): ResourceEmployee {
        return this.toEmployee(dto);
    }

    getAssignment(id: number): Observable<PlanningAssignment> {
        return this.http
            .get<PlanningAssignmentDto>(`${this.apiUrl}/assignments/${id}`)
            .pipe(map(dto => this.fromDto(dto)));
    }

    /** id === 0 legt neu an, sonst wird der bestehende Termin ersetzt. */
    saveAssignment(assignment: PlanningAssignment): Observable<PlanningAssignment> {
        const payload = this.toSaveDto(assignment);

        const request$ = assignment.id > 0
            ? this.http.put<PlanningAssignmentDto>(`${this.apiUrl}/assignments/${assignment.id}`, payload)
            : this.http.post<PlanningAssignmentDto>(`${this.apiUrl}/assignments`, payload);

        return request$.pipe(map(dto => this.fromDto(dto)));
    }

    /** sendCancellation=true verschickt vorher eine Outlook-Absage (nur wenn eine Einladung versendet wurde). */
    deleteAssignment(id: number, sendCancellation = false): Observable<void> {
        const params = new HttpParams().set('sendCancellation', sendCancellation);
        return this.http.delete<void>(`${this.apiUrl}/assignments/${id}`, { params });
    }

    /** Outlook-Einladung für einen bestehenden Termin (erneut) verschicken. */
    sendInvite(id: number): Observable<PlanningAssignment> {
        return this.http
            .post<PlanningAssignmentDto>(`${this.apiUrl}/assignments/${id}/invite`, {})
            .pipe(map(dto => this.fromDto(dto)));
    }

    // ---- Mapping ---------------------------------------------------------------

    private toEmployee(dto: ResourceEmployeeDto): ResourceEmployee {
        const name = dto.name || dto.email || dto.id;
        return {
            id: dto.id,
            name,
            email: dto.email,
            initials: this.initialsOf(name),
            knowledgeIds: [...(dto.skillIds ?? [])],
            color: this.colorFor(dto.id),
            weeklyHours: dto.weeklyCapacityHours > 0 ? dto.weeklyCapacityHours : 38.5
        };
    }

    fromDto(dto: PlanningAssignmentDto): PlanningAssignment {
        const weekendDays: ResourceWeekendDay[] = [];

        for (const entry of dto.weekendHours ?? []) {
            const date = entry.date.substring(0, 10);
            let day = weekendDays.find(d => d.date === date);
            if (!day) {
                day = { date, hoursByEmployee: {} };
                weekendDays.push(day);
            }
            day.hoursByEmployee[entry.userId] = entry.hours;
        }

        weekendDays.sort((a, b) => a.date.localeCompare(b.date));

        return {
            id: dto.id,
            teamId: dto.teamId ?? null,
            title: dto.title,
            startDate: dto.start.substring(0, 10),
            endDate: dto.end.substring(0, 10),
            startTime: dto.start.substring(11, 16),
            endTime: dto.end.substring(11, 16),
            categoryId: dto.categoryId,
            category: dto.categoryName ?? '',
            color: dto.color ?? dto.categoryColor ?? '#2563eb',
            participants: (dto.participants ?? []).map(p => ({
                employeeId: p.userId,
                utilizationPercent: p.utilizationPercent
            })),
            weekendDates: weekendDays.map(d => d.date),
            weekendDays,
            customerId: dto.customerId,
            plantId: dto.equipmentId,
            location: dto.location ?? '',
            sendOutlookInvitation: dto.sendOutlookInvitation,

            sourceExternalUid: dto.sourceExternalUid ?? null,
            sourceExternalUserId: dto.sourceExternalUserId ?? null,
            sourceExternalStart: dto.sourceExternalStart ?? null,
            canEdit: dto.canEdit ?? undefined
        };
    }

    toSaveDto(assignment: PlanningAssignment): PlanningAssignmentSaveDto {
        const weekendHours: ResourceWeekendHoursDto[] = [];

        // Je ausgewähltem Wochenendtag und Teilnehmer ein Eintrag (auch 0 h),
        // damit die Auswahl nach dem Neuladen erhalten bleibt.
        for (const date of assignment.weekendDates ?? []) {
            const day = (assignment.weekendDays ?? []).find(d => d.date === date);

            for (const participant of assignment.participants) {
                const hours = Number(day?.hoursByEmployee[participant.employeeId] ?? 0);
                weekendHours.push({
                    userId: participant.employeeId,
                    date,
                    hours: Number.isFinite(hours) ? hours : 0
                });
            }
        }

        return {
            title: assignment.title,
            categoryId: assignment.categoryId,
            start: `${assignment.startDate}T${assignment.startTime}:00`,
            end: `${assignment.endDate}T${assignment.endTime}:00`,
            customerId: assignment.customerId,
            equipmentId: assignment.plantId,
            location: assignment.location?.trim() || null,
            color: assignment.color || null,
            sendOutlookInvitation: assignment.sendOutlookInvitation,
            participants: assignment.participants.map(p => ({
                userId: p.employeeId,
                utilizationPercent: Number(p.utilizationPercent)
            })),
            weekendHours,
            sourceExternalUid: assignment.sourceExternalUid ?? null,
            sourceExternalUserId: assignment.sourceExternalUserId ?? null,
            sourceExternalStart: assignment.sourceExternalStart ?? null
        };
    }

    private initialsOf(name: string): string {
        const parts = name
            .split(/[\s@._\-]+/)
            .filter(part => part.length > 0);

        if (parts.length === 0) {
            return '?';
        }

        const initials = parts.length === 1
            ? parts[0].substring(0, 2)
            : parts[0].charAt(0) + parts[1].charAt(0);

        return initials.toUpperCase();
    }

    private colorFor(id: string): string {
        let hash = 0;
        for (let i = 0; i < id.length; i++) {
            hash = (hash * 31 + id.charCodeAt(i)) | 0;
        }
        return this.palette[Math.abs(hash) % this.palette.length];
    }
}
